import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState, useRecoilState, atom } from 'recoil';
import {
  AddressInfo,
  useCurrentUserId,
  connectedDictAtom,
  connectedAddressAtom,
} from '../utils/address';
import { useDeviceTypeIsMobile } from './useDeviceType';

export const useCurrentAddressInfo = (): AddressInfo => {
  const address = useCurrentUserId();
  const connectedDict = useRecoilValue(connectedDictAtom);
  const isMobile = useDeviceTypeIsMobile();

  if (!address || !connectedDict) {
    return {
      address: '',
      isVerified: false,
    };
  }

  const info = connectedDict[address];

  // 如果没有 verified 就不返回了，只展示登陆用户
  const verified = !!info?.sessionId;

  return isMobile || verified
    ? {
        ...info,
        // 免得被直接修改引用
        account: { ...info?.account },
        isVerified: !!info?.sessionId,
        address,
      }
    : { address: '', isVerified: false };
};

export const useConnectedAddress = () => {
  return useRecoilValue(connectedAddressAtom);
};

export const useLoginedAddresses = () => {
  const connectedDict = useRecoilValue(connectedDictAtom);
  const loginedUsers = connectedDict
    ? Object.entries(connectedDict)
        .filter(([k, v]) => !!v.sessionId)
        .map(([k, v]) => ({
          id: k,
          ...v,
        }))
    : [];

  return loginedUsers;
};

// assign 上去的，如果新的 info 中没有 key，原来的还保留
export const useUpdateCurrentAddressInfo = () => {
  const address = useCurrentUserId();
  const setConnectedDict = useSetRecoilState(connectedDictAtom);

  return useCallback(
    (info: AddressInfo | ((arg: AddressInfo) => AddressInfo)) => {
      if (!address) {
        return;
      }

      if (typeof info === 'function') {
        setConnectedDict(pre => ({
          ...pre,
          [address]: {
            ...pre[address],
            ...info(pre[address]),
          },
        }));

        return;
      }

      setConnectedDict(pre => ({
        ...pre,
        [address]: {
          ...pre[address],
          ...info,
        },
      }));
    },
    [address, setConnectedDict],
  );
};

export const useConnectDict = () => {
  return useRecoilValue(connectedDictAtom);
};

export const useConnectDictAtom = () => {
  return useRecoilState(connectedDictAtom);
};

const ProfileAddressAtom = atom({
  key: 'profileAddress',
  default: '',
});

export const useProfileAddress = () => {
  return useRecoilState(ProfileAddressAtom);
};
